import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageTemplate from '../PageTemplate';
import * as api from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorMessage from '../../components/ErrorMessage';
import './CaseStudyDetail.css';

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getCurrentLang = () => {
    return i18n.language === 'zh-CN' ? 'zh_CN' : 'en_US';
  };

  const lang = getCurrentLang();

  useEffect(() => {
    const fetchCaseStudy = async () => {
      try {
        setLoading(true);
        const data = await api.getCaseStudyBySlug(slug);
        setCaseStudy(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching case study:', err);
        setError('Failed to load case study');
      } finally {
        setLoading(false);
      }
    };

    fetchCaseStudy();
  }, [slug]);

  if (loading) {
    return (
      <PageTemplate>
        <LoadingSpinner />
      </PageTemplate>
    );
  }

  if (error || !caseStudy) {
    return (
      <PageTemplate>
        <ErrorMessage message={error || 'Case study not found'} />
      </PageTemplate>
    );
  }

  const breadcrumbs = [
    { labelKey: 'nav.caseStudies', href: '/case-studies/all' },
    { label: caseStudy.client_name[lang] }
  ];

  return (
    <PageTemplate 
      breadcrumbs={breadcrumbs}
      customTitle={caseStudy.client_name[lang]}
    >
      <article className="case-study-detail">
        {/* 案例头部 */}
        <header className="case-study-detail-header">
          <span className="case-study-detail-industry">{caseStudy.industry[lang]}</span>
          
          {caseStudy.featured && (
            <div className="case-study-detail-featured">
              ⭐ {t('pages.featuredCases.featured')}
            </div>
          )}
        </header>

        {/* 挑战 */}
        <section className="case-study-section">
          <h2 className="section-title">{t('pages.caseStudyDetail.challenge')}</h2>
          <p className="section-content">{caseStudy.challenge[lang]}</p>
        </section>

        {/* 解决方案 */}
        {caseStudy.solution && (
          <section className="case-study-section">
            <h2 className="section-title">{t('pages.caseStudyDetail.solution')}</h2>
            <p className="section-content">{caseStudy.solution[lang]}</p>
          </section>
        )}

        {/* 关键成果 */}
        <section className="case-study-section">
          <h2 className="section-title">{t('pages.allCases.keyResults')}</h2>
          <ul className="results-list">
            {caseStudy.results.map((result, index) => (
              <li key={index}>{result[lang]}</li>
            ))}
          </ul>
        </section>

        {/* 时间线 */}
        {caseStudy.timeline && (
          <section className="case-study-section">
            <h2 className="section-title">{t('pages.caseStudyDetail.timeline')}</h2>
            <p className="section-content">{caseStudy.timeline[lang]}</p>
          </section>
        )}
      </article>
    </PageTemplate>
  );
};

export default CaseStudyDetail;
